/**
 * Shared helper utilities for React Query hooks
 *
 * Centralizes common functionality across all API queries to ensure consistency
 * and eliminate code duplication. This file can be extended with additional
 * helper functions as needed.
 */

/**
 * Creates a retry handler for React Query hooks
 *
 * @param includeNotFound - Whether to include 404 errors in the "don't retry" list
 * @returns A retry function that can be used in useQuery configurations
 *
 * @example
 * // For list endpoints (don't retry on 404)
 * retry: createRetryHandler(false)
 *
 * // For detail endpoints (don't retry on 404)
 * retry: createRetryHandler(true)
 */
export const createRetryHandler =
  (includeNotFound = false) =>
  (failureCount: number, error: unknown) => {
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status: number }).status;
      const shouldNotRetry =
        status === 401 || status === 403 || (includeNotFound && status === 404);
      if (shouldNotRetry) {
        return false;
      }
    }
    return failureCount < 2;
  };

/**
 * Pre-configured retry handlers for common use cases
 */
export const retryHandlers = {
  /**
   * For list endpoints - don't retry on auth errors only
   */
  list: createRetryHandler(false),

  /**
   * For detail endpoints - don't retry on auth errors and 404
   */
  detail: createRetryHandler(true),
} as const;
